import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRight, Search } from "lucide-react";
import { runsApi, remediationsApi, workspacesApi } from "../api/endpoints";
import { SkeletonCard, SkeletonTable } from "../components/states/Skeleton";
import { ErrorState } from "../components/states/ErrorState";
import { EmptyState } from "../components/states/EmptyState";
import { AuditStrip } from "../components/audit/AuditStrip";
import { FeasibilityBadge } from "../components/chips/FeasibilityBadge";
import { SeverityChip } from "../components/chips/SeverityChip";
import { FactChip } from "../components/chips/FactChip";
import { ProvenancePopover } from "../components/provenance/ProvenancePopover";
import { ExplainFindingDialog } from "../components/ai/ExplainFindingDialog";
import { ValidationSuggestionDialog } from "../components/ai/ValidationSuggestionDialog";
import { RemediationDialog } from "../components/remediation/RemediationDialog";
import { formatDateTime, formatMinutes } from "../lib/format";
import { useEngineMeta } from "../components/shell/AppShell";
import { cn } from "../lib/utils";

const TABS = ["processes", "findings", "dataset"];

function TabBar({ value, onChange }) {
  return (
    <div
      role="tablist"
      data-testid="run-tabs"
      className="flex gap-1 border-b border-slate-200"
    >
      {TABS.map((t) => (
        <button
          key={t}
          role="tab"
          data-testid={`run-tab-${t}`}
          aria-selected={value === t}
          onClick={() => onChange(t)}
          className={cn(
            "px-3 py-2 text-sm font-medium capitalize border-b-2 -mb-px",
            value === t
              ? "border-slate-900 text-slate-900"
              : "border-transparent text-slate-500 hover:text-slate-900",
          )}
        >
          {t}
        </button>
      ))}
    </div>
  );
}

export default function RunDetailPage() {
  const { workspaceId, runId } = useParams();
  const [run, setRun] = useState(null);
  const [processes, setProcesses] = useState(null);
  const [findings, setFindings] = useState(null);
  const [dataset, setDataset] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("processes");
  const [severityFilter, setSeverityFilter] = useState("");
  const [processFilter, setProcessFilter] = useState("");
  const { setEngineMeta } = useEngineMeta();
  const [remediationsByFinding, setRemediationsByFinding] = useState({});

  useEffect(() => {
    let mounted = true;
    setError(null);
    Promise.all([
      runsApi.get(workspaceId, runId),
      runsApi.processes(workspaceId, runId),
      runsApi.findings(workspaceId, runId, { page_size: 500 }),
      runsApi.dataset(workspaceId, runId),
      remediationsApi.list(workspaceId, { run_id: runId, size: 200 }).catch(() => ({ items: [] })),
      workspacesApi.get(workspaceId).catch(() => null),
    ])
      .then(([r, p, f, d, rems, ws]) => {
        if (!mounted) return;
        setRun(r);
        setProcesses(p);
        setFindings(f);
        setDataset(d);
        setWorkspace(ws);
        const byId = {};
        for (const item of rems?.items || []) {
          byId[item.finding_id] = item;
        }
        setRemediationsByFinding(byId);
        setEngineMeta({
          engine_version: r.engine_version,
          rule_library_version: r.rule_library_version,
          rule_library_hash: r.rule_library_hash,
        });
      })
      .catch((e) => mounted && setError(e));
    return () => {
      mounted = false;
      setEngineMeta(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, runId]);

  const filteredFindings = useMemo(() => {
    let items = findings?.items || [];
    if (severityFilter) {
      items = items.filter((f) => f.effective_severity === severityFilter);
    }
    if (processFilter) {
      items = items.filter(
        (f) =>
          (f.process_id || "").includes(processFilter) ||
          (f.process_ids || []).some((p) => p.includes(processFilter)),
      );
    }
    return items;
  }, [findings, severityFilter, processFilter]);

  if (error) return <ErrorState error={error} onRetry={() => window.location.reload()} />;
  if (!run) return <SkeletonCard />;

  return (
    <div className="space-y-6">
      <header className="space-y-3">
        <div className="text-xs uppercase tracking-widest text-slate-500">
          <Link
            to={`/w/${workspaceId}/analyses`}
            className="hover:text-slate-900"
          >
            Analyses
          </Link>
        </div>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1
              data-testid="run-detail-name"
              className="text-2xl font-semibold text-slate-900"
            >
              {run.name}
            </h1>
            <div className="mt-1 text-sm text-slate-500">
              {formatDateTime(run.created_at)} · status{" "}
              <span className="font-medium text-slate-700">{run.status}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {workspace && ["professional", "enterprise"].includes(workspace.plan) ? (
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/workspaces/${workspaceId}/runs/${run.id}/recovery_assurance_report.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="download-recovery-assurance-report"
                className="inline-flex items-center gap-1.5 rounded bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500"
                title="Full Recovery Assurance report — 14 sections, deterministic, no LLM on render"
              >
                <ReportIcon />
                Download Recovery Assurance Report
              </a>
            ) : (
              <button
                type="button"
                disabled
                data-testid="download-recovery-assurance-report"
                data-locked="true"
                title="Available on Professional and Enterprise plans"
                className="inline-flex items-center gap-1.5 rounded bg-slate-200 px-3 py-1.5 text-xs font-medium text-slate-500 cursor-not-allowed"
              >
                <LockIcon />
                Recovery Assurance Report · upgrade
              </button>
            )}
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/workspaces/${workspaceId}/runs/${run.id}/audit_receipt.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="download-audit-receipt"
              className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-800 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
              title="One-page audit receipt for this run"
            >
              <ReceiptIcon />
              Download audit receipt
            </a>
          </div>
        </div>
        <AuditStrip run={run} />
      </header>

      <div className="grid gap-3 sm:grid-cols-4">
        {[
          ["Rules evaluated", run.summary?.rules_evaluated || 0],
          ["Passed", run.summary?.passed || 0],
          ["Triggered", run.summary?.triggered || 0],
          ["Not evaluated", run.summary?.not_evaluated || 0],
        ].map(([k, v]) => (
          <div
            key={k}
            data-testid={`summary-${k.toLowerCase().replace(/\s+/g, "-")}`}
            className="rounded border border-slate-200 bg-white p-4"
          >
            <div className="text-[11px] uppercase tracking-wider text-slate-500">
              {k}
            </div>
            <div className="mt-1 text-2xl font-semibold text-slate-900">{v}</div>
          </div>
        ))}
      </div>

      <TabBar value={tab} onChange={setTab} />

      {tab === "processes" && (
        <ProcessesTable
          workspaceId={workspaceId}
          runId={runId}
          processes={processes}
          findings={findings?.items || []}
        />
      )}
      {tab === "findings" && (
        <FindingsTab
          workspaceId={workspaceId}
          runId={runId}
          filteredFindings={filteredFindings}
          allFindings={findings?.items || []}
          severityFilter={severityFilter}
          setSeverityFilter={setSeverityFilter}
          processFilter={processFilter}
          setProcessFilter={setProcessFilter}
          remediationsByFinding={remediationsByFinding}
        />
      )}
      {tab === "dataset" && <DatasetTab dataset={dataset} />}
    </div>
  );
}

function ProcessesTable({ workspaceId, runId, processes, findings }) {
  if (!processes) return <SkeletonTable rows={8} cols={6} />;
  if (processes.length === 0)
    return <EmptyState title="No processes in this run" />;
  const findingsByProcess = findings.reduce((acc, f) => {
    if (f.scope !== "process" || !f.process_id) return acc;
    acc[f.process_id] = (acc[f.process_id] || 0) + 1;
    return acc;
  }, {});
  return (
    <div className="rounded border border-slate-200 bg-white overflow-hidden">
      <div className="overflow-x-auto">
        <table
          data-testid="processes-table"
          className="w-full text-sm"
        >
          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-2.5">Process</th>
              <th className="px-4 py-2.5">Overall status</th>
              <th className="px-4 py-2.5">Confidence</th>
              <th className="px-4 py-2.5">Data completeness</th>
              <th className="px-4 py-2.5">Findings</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {processes.map((p) => (
              <tr
                key={p.process_id}
                data-testid={`process-row-${p.process_id}`}
                className="hover:bg-slate-50"
              >
                <td className="px-4 py-3">
                  <Link
                    to={`/w/${workspaceId}/analyses/${runId}/processes/${p.process_id}`}
                    className="font-medium text-slate-900 hover:underline"
                  >
                    {p.process_name || p.process_id}
                  </Link>
                  <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                    {p.process_id}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <FeasibilityBadge status={p.overall_status} />
                </td>
                <td className="px-4 py-3 text-slate-700">
                  {p.feasibility_confidence}
                </td>
                <td className="px-4 py-3 text-slate-700">
                  {p.data_completeness_pct === null ||
                  p.data_completeness_pct === undefined
                    ? "—"
                    : `${Math.round(p.data_completeness_pct)}%`}
                </td>
                <td className="px-4 py-3 text-slate-700">
                  {findingsByProcess[p.process_id] || 0}
                </td>
                <td className="px-4 py-3 text-right">
                  <Link
                    to={`/w/${workspaceId}/analyses/${runId}/processes/${p.process_id}`}
                    className="inline-flex items-center text-slate-500 hover:text-slate-900"
                    aria-label="Open process detail"
                  >
                    <ChevronRight size={16} />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FindingsTab({
  workspaceId, runId, filteredFindings, severityFilter, setSeverityFilter,
  processFilter, setProcessFilter,
  remediationsByFinding = {},
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <label className="inline-flex items-center gap-1 text-xs text-slate-600">
          Severity
          <select
            data-testid="findings-severity-filter"
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            className="rounded border border-slate-300 bg-white px-2 py-1 text-sm"
          >
            <option value="">All</option>
            <option value="CRITICAL">Critical</option>
            <option value="HIGH">High</option>
            <option value="MEDIUM">Medium</option>
            <option value="LOW">Low</option>
            <option value="REVIEW">Review</option>
          </select>
        </label>
        <div className="relative">
          <Search
            size={14}
            className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            data-testid="findings-process-filter"
            value={processFilter}
            onChange={(e) => setProcessFilter(e.target.value)}
            placeholder="Filter by process id…"
            className="rounded border border-slate-300 bg-white pl-7 pr-2 py-1 text-sm w-56"
          />
        </div>
        <span className="text-xs text-slate-500">
          {filteredFindings.length} finding{filteredFindings.length === 1 ? "" : "s"}
        </span>
      </div>
      {filteredFindings.length === 0 ? (
        <EmptyState title="No findings match those filters" />
      ) : (
        <ul className="space-y-3" data-testid="findings-list">
          {filteredFindings.map((f) => (
            <FindingCard
              key={f.id}
              finding={f}
              workspaceId={workspaceId}
              runId={runId}
              remediation={remediationsByFinding[f.id]}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

export function FindingCard({ finding, workspaceId, runId, remediation }) {
  const [current, setCurrent] = useState(remediation || null);
  useEffect(() => setCurrent(remediation || null), [remediation]);
  return (
    <li
      data-testid={`finding-card-${finding.id}`}
      className="rounded border border-slate-200 bg-white p-4"
    >
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <SeverityChip severity={finding.effective_severity} />
          <FactChip source="FINDING" />
          <RemediationDialog
            workspaceId={workspaceId}
            runId={runId}
            finding={finding}
            currentStatus={current?.status || "open"}
            currentRemediation={current}
            onSaved={(saved) => setCurrent(saved)}
          />
          <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] text-slate-700">
            {finding.rule_id} · v{finding.rule_version}
          </span>
          {finding.escalation_reason && (
            <span
              className="text-xs text-slate-500"
              title={finding.escalation_reason}
            >
              base: {finding.base_severity}
              {finding.base_severity !== finding.effective_severity && (
                <> → {finding.effective_severity}</>
              )}
            </span>
          )}
        </div>
        {finding.process_id && (
          <Link
            to={`/w/${workspaceId}/analyses/${runId}/processes/${finding.process_id}`}
            data-testid={`finding-process-link-${finding.id}`}
            className="text-xs text-slate-600 underline hover:text-slate-900"
          >
            {finding.process_id}
          </Link>
        )}
      </div>
      <p className="mt-2 text-sm text-slate-800">{finding.calculation}</p>
      {finding.remediation_hint && (
        <p className="mt-2 text-xs text-slate-500 italic">
          Suggested remediation: {finding.remediation_hint}
        </p>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <ExplainFindingDialog
          workspaceId={workspaceId}
          runId={runId}
          finding={finding}
        />
        <ValidationSuggestionDialog
          workspaceId={workspaceId}
          runId={runId}
          finding={finding}
        />
      </div>
      {finding.facts_used && Object.keys(finding.facts_used).length > 0 && (
        <details className="mt-3 group">
          <summary
            className="cursor-pointer text-xs text-slate-600 hover:text-slate-900"
            data-testid={`finding-facts-toggle-${finding.id}`}
          >
            Facts used ({Object.keys(finding.facts_used).length})
          </summary>
          <ul
            className="mt-2 space-y-1 text-[11px]"
            data-testid={`finding-facts-list-${finding.id}`}
          >
            {Object.entries(finding.facts_used).map(([k, v]) => (
              <li
                key={k}
                data-testid={`finding-fact-${finding.id}-${k}`}
                className="flex items-center gap-2 rounded bg-slate-50 px-2 py-1"
              >
                <FactChip source="CALCULATED" />
                <code className="font-mono text-slate-700">{k}</code>
                <span className="text-slate-500">=</span>
                <code className="font-mono text-slate-900">
                  {v === null || v === undefined
                    ? "—"
                    : typeof v === "object"
                      ? JSON.stringify(v)
                      : String(v)}
                </code>
                <ProvenancePopover
                  mode="finding"
                  workspaceId={workspaceId}
                  runId={runId}
                  findingId={finding.id}
                  field={k}
                  fallbackLabel="source"
                  ariaLabel={`Show provenance for ${k}`}
                />
              </li>
            ))}
          </ul>
        </details>
      )}
    </li>
  );
}

function DatasetTab({ dataset }) {
  if (!dataset) return <SkeletonTable rows={6} cols={4} />;
  const canonical = dataset.canonical_data || {};
  const groups = Object.entries(canonical).filter(
    ([_, v]) => Array.isArray(v) && v.length > 0,
  );
  if (groups.length === 0)
    return <EmptyState title="No canonical data recorded for this run" />;
  return (
    <div className="space-y-4">
      {groups.map(([entity, rows]) => (
        <div
          key={entity}
          data-testid={`dataset-group-${entity}`}
          className="rounded border border-slate-200 bg-white overflow-hidden"
        >
          <div className="border-b border-slate-100 bg-slate-50 px-4 py-2 text-xs uppercase tracking-wider text-slate-500">
            {entity} ({rows.length})
          </div>
          <div className="max-h-80 overflow-auto">
            <table className="w-full text-xs">
              <thead className="bg-white text-left text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  {Object.keys(rows[0]).slice(0, 8).map((k) => (
                    <th key={k} className="px-3 py-1.5 font-medium">
                      {k}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.slice(0, 200).map((r, i) => (
                  <tr key={i}>
                    {Object.keys(rows[0]).slice(0, 8).map((k) => (
                      <td key={k} className="px-3 py-1.5 text-slate-700 font-mono">
                        {formatCell(r[k])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

function formatCell(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "yes" : "no";
  if (Array.isArray(v)) return v.join("; ");
  if (typeof v === "object") return JSON.stringify(v);
  return String(v).slice(0, 60);
}


function ReceiptIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 2h13a1 1 0 0 1 1 1v18l-3-2-2 2-2-2-2 2-2-2-3 2V3a1 1 0 0 1 1-1z" />
      <path d="M8 7h6" />
      <path d="M8 11h6" />
      <path d="M8 15h4" />
    </svg>
  );
}

function ReportIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="14" y2="17" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width="12"
      height="12"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}
